'use client'

import { useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import type { ActionItem, Insight } from '@/types/radar'
import { sortActions } from '@/lib/radar/action-priority'
import { EFFORT_LABELS } from '@/lib/radar/scoring'
import { Chip, Panel } from './ui'

/**
 * Cross-tool insights and the ranked issue list, behind two tabs.
 *
 * Passed items are collapsed behind a "show more" control by default: a
 * customer opening this panel is looking for what is wrong, and 5 green rows
 * above the first error buries it.
 *
 * Tabs are a real ARIA tablist with roving arrow-key focus — the underline is
 * the only visual state, so the semantics have to carry the rest.
 */

const SEVERITY = {
  bad: { label: 'Error', color: 'var(--cta)', icon: XCircle },
  conflict: {
    label: 'Warning',
    color: 'var(--warning-text)',
    icon: AlertTriangle,
  },
  good: { label: 'Passed', color: 'var(--growth)', icon: CheckCircle2 },
} as const

type TabId = 'insights' | 'issues'

export function InsightsTabs({
  insights,
  actions,
}: {
  insights: Insight[]
  actions: ActionItem[]
}) {
  const [tab, setTab] = useState<TabId>('insights')
  const [showPassed, setShowPassed] = useState(false)

  const { problems, passed } = useMemo(() => {
    const order = { bad: 0, conflict: 1, good: 2 }
    const sorted = [...insights].sort((a, b) => order[a.type] - order[b.type])
    return {
      problems: sorted.filter(i => i.type !== 'good'),
      passed: sorted.filter(i => i.type === 'good'),
    }
  }, [insights])

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: 'insights', label: 'Cross-tool insights', count: insights.length },
    { id: 'issues', label: 'Top issues', count: actions.length },
  ]

  function onTabKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const i = tabs.findIndex(t => t.id === tab)
    const next = e.key === 'ArrowRight' ? i + 1 : i - 1
    const target = tabs[(next + tabs.length) % tabs.length]
    setTab(target.id)
    document.getElementById(`radar-tab-${target.id}`)?.focus()
  }

  if (insights.length === 0 && actions.length === 0) return null

  return (
    <Panel>
      <div
        role='tablist'
        aria-label='Insights and issues'
        onKeyDown={onTabKeyDown}
        className='flex items-center gap-1 overflow-x-auto border-b border-hairline px-6 sm:px-7'
      >
        {tabs.map(t => {
          const active = tab === t.id
          return (
            <button
              key={t.id}
              id={`radar-tab-${t.id}`}
              role='tab'
              type='button'
              aria-selected={active}
              aria-controls={`radar-panel-${t.id}`}
              tabIndex={active ? 0 : -1}
              onClick={() => setTab(t.id)}
              className={`-mb-px whitespace-nowrap border-b-2 px-3 py-3.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
                active
                  ? 'border-primary font-semibold text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label} ({t.count})
            </button>
          )
        })}
      </div>

      <div
        role='tabpanel'
        id='radar-panel-insights'
        aria-labelledby='radar-tab-insights'
        hidden={tab !== 'insights'}
      >
        {insights.length === 0 ? (
          <EmptyRow>No cross-tool insights for this run.</EmptyRow>
        ) : (
          <>
            {problems.map((ins, i) => (
              <InsightRow key={`p-${i}`} insight={ins} />
            ))}
            {showPassed &&
              passed.map((ins, i) => (
                <InsightRow key={`g-${i}`} insight={ins} />
              ))}
            {passed.length > 0 && (
              <div className='px-6 py-3.5 text-center text-[13px] sm:px-7'>
                <button
                  type='button'
                  onClick={() => setShowPassed(v => !v)}
                  aria-expanded={showPassed}
                  className='text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
                >
                  {showPassed
                    ? `Hide ${passed.length} passed ${passed.length === 1 ? 'item' : 'items'}`
                    : `Show ${passed.length} more passed ${passed.length === 1 ? 'item' : 'items'}`}
                </button>
              </div>
            )}
            {problems.length === 0 && !showPassed && (
              <EmptyRow>Nothing failing. All checks passed.</EmptyRow>
            )}
          </>
        )}
      </div>

      <div
        role='tabpanel'
        id='radar-panel-issues'
        aria-labelledby='radar-tab-issues'
        hidden={tab !== 'issues'}
      >
        {actions.length === 0 ? (
          <EmptyRow>No open issues.</EmptyRow>
        ) : (
          sortActions(actions).map(action => (
            <ActionRow key={action.id} action={action} />
          ))
        )}
      </div>
    </Panel>
  )
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <span className='block px-6 py-6 text-center text-[13px] text-instrument sm:px-7'>
      {children}
    </span>
  )
}

function InsightRow({ insight }: { insight: Insight }) {
  const meta = SEVERITY[insight.type]
  const Icon = meta.icon
  return (
    <div className='flex items-start gap-4 border-b border-hairline px-6 py-4 transition-colors last:border-b-0 hover:bg-surface-raised sm:gap-5 sm:px-7'>
      <Chip color={meta.color} className='mt-0.5 gap-1'>
        <Icon size={10} aria-hidden='true' />
        {meta.label}
      </Chip>
      <div className='flex min-w-0 flex-col gap-1.5'>
        <span className='text-sm font-semibold'>{insight.title}</span>
        <span className='max-w-[68ch] text-pretty text-[13px] leading-relaxed text-muted-foreground'>
          {insight.message}
        </span>
      </div>
    </div>
  )
}

function ActionRow({ action }: { action: ActionItem }) {
  const effort = EFFORT_LABELS[action.effort]
  return (
    <div className='flex items-start gap-4 border-b border-hairline px-6 py-4 transition-colors last:border-b-0 hover:bg-surface-raised sm:gap-5 sm:px-7'>
      {/* Effort, not impact. The impact chip claimed a measured outcome
          from a hand-authored string — see action-priority.ts. */}
      <Chip color='var(--pm-text-muted)' className='mt-0.5'>
        {action.effort === 'low'
          ? 'Quick'
          : action.effort === 'medium'
            ? 'Med'
            : 'Deep'}
      </Chip>
      <div className='flex min-w-0 flex-col gap-1.5'>
        <span className='text-sm font-semibold'>{action.title}</span>
        <span className='max-w-[68ch] text-pretty text-[13px] leading-relaxed text-muted-foreground'>
          {action.why}
        </span>
        <span className='instrument-label text-instrument'>{effort.label}</span>
      </div>
    </div>
  )
}
